// Rasterize a share card element to a PNG blob.
// Cards laid out at a reduced size (360x640) are scaled up on capture; cards
// already authored at their export size (1080x1920 / 1080x1350) pass scale: 1.
export const generateShareImage = async (
  element: HTMLElement,
  options: { scale?: number } = {}
): Promise<Blob | null> => {
  try {
    const { domToCanvas } = await import('modern-screenshot');

    const scale = options.scale ?? 3;

    // Capture the element with modern-screenshot at high resolution
    const canvas = await domToCanvas(element, {
      backgroundColor: '#0A1128',
      scale,
    });

    // Convert to blob
    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/png', 1);
    });
  } catch (error) {
    console.error('Error generating share image:', error);
    return null;
  }
};

// Share image via Web Share API (works on mobile)
export const shareImageFile = async (blob: Blob, title: string, text: string): Promise<boolean> => {
  // Early exit if Web Share API is not available at all
  if (typeof navigator.share !== 'function') {
    return false;
  }

  try {
    const file = new File([blob], 'bt-vision-stats.png', { type: 'image/png' });

    const shareData = {
      title,
      text,
      files: [file],
    };

    await navigator.share(shareData);
    return true;
  } catch (error) {
    const err = error as Error;

    // AbortError means user cancelled the share dialog - this is expected behavior
    if (err.name === 'AbortError') {
      return true;
    }

    console.error('Error sharing image:', err.name, err.message);
    return false;
  }
};

// Download image to device
export const downloadImage = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.download = `${filename}.png`;
  link.href = url;
  link.click();
  URL.revokeObjectURL(url);
};
